import { useConfigStore } from '@/store/config.store';
import { useField } from '@/composables/useField';
import { useMaterialUpdater } from '@/composables/useMaterialUpdater';
import { LOCKED_FOREGROUND_GROUP_NAME } from '@/littleWedding/fabricGroupsName';
import { createLockedBackgroundObjectGroup } from '@/commons/fabricjs/FabricGroupHelper';
import { getGridUrl } from '@/utils/getGridUrl';
import { dataStore } from '@/commons/DataStore';
import { storeToRefs } from 'pinia';
import { computed, type Ref } from 'vue';
import type { IFabricWorldInitParams } from '@/commons/fabricjs/IFabricWorldInitParams';

async function initCanvas(fabricElementRef: Ref<HTMLCanvasElement | null>, currentSide: 'recto' | 'verso'): Promise<() => void> {
    const { displayGrid, fZoom, height, isAdmin, isProductEdition, productImages, productType, width } = storeToRefs(useConfigStore());
    const { filteredFields, getFieldsRecto, getFieldsVerso, loadFields, updateFieldsAndOrder } = useField();
    const fieldsByFormat = computed(() => {
        return currentSide === 'verso' ? getFieldsVerso() : getFieldsRecto();
    });
    const fabricWorld = dataStore.getFabricWorld(currentSide);
    // eslint-disable-next-line prefer-destructuring
    const updateMaterial = useMaterialUpdater(filteredFields, fabricWorld).updateMaterial;
    const fabricParams: IFabricWorldInitParams = {
        canvas: null,
        enableRetinaScaling: true,
        includeDefaultValues: false, // do not export default values toJSON (shorter json)
        absolutePan: true,
        selection: false,
        allowTouchScrolling: false, // Changed: disable to prevent conflicts
        allowTouchPan: true,
        preserveObjectStacking: true,
        productType: productType.value,
        backgroundColor: 'transparent',
        transparentBackground: true,
        width: width.value,
        height: height.value,
        parentBackgroundColor: getParentBackgroundColor(isProductEdition.value, isAdmin.value),
    };
    fabricWorld.init({ ...fabricParams, canvas: fabricElementRef.value });

    fabricWorld.displayImageFullCanvas();
    if (fabricWorld.fabricCanvas) {
        createLockedBackgroundObjectGroup(fabricWorld.fabricCanvas, LOCKED_FOREGROUND_GROUP_NAME);
    }
    const gridUrl = await getGridUrl(currentSide, productType.value, productImages.value);
    if (gridUrl) {
        await fabricWorld.createGridHelper(gridUrl, isProductEdition.value);
        fabricWorld.setGridHelperVisibility(true);
        displayGrid.value = true;
    }
    fZoom.value = fabricWorld.fabricCanvas?.getZoom() ?? 1;

    if (filteredFields.value.length) {
        await loadFields(
            fieldsByFormat.value,
            {
                fabricWorld,
                select: false,
                isAdmin: isAdmin.value,
                isProductEdition: isProductEdition.value,
            },
            updateMaterial,
        );
        updateFieldsAndOrder(fabricWorld, fieldsByFormat.value);
        fabricWorld.displayImageFullCanvas();
    } else {
        fabricWorld.displayImageFullCanvas();
    }

    updateMaterial();
    return updateMaterial;
}

function getParentBackgroundColor(isProductEdition: boolean, isAdmin: boolean): string {
    if (isProductEdition) {
        return '#D2E5F6';
    }
    if (isAdmin) {
        return '#E2E7ff';
    }
    return '#E2E7EA';
}
export { getParentBackgroundColor, initCanvas };
